import { useCallback, useState } from "react";
import { X } from "lucide-react";

interface Props {
  tags: string[];
  onChange: (next: string[]) => void;
}

/**
 * Chip input for blog post tags. Enter or comma commits a chip,
 * Backspace on an empty input removes the last one. Mirrors the
 * admin's TagInput behavior; the resolver does the find-or-create
 * on save.
 */
export function TagInput({ tags, onChange }: Props) {
  const [draft, setDraft] = useState("");

  const addTag = useCallback(
    (value: string) => {
      const trimmed = value.trim().replace(/,+$/g, "");
      if (!trimmed) return;
      if (tags.includes(trimmed)) {
        setDraft("");
        return;
      }
      onChange([...tags, trimmed]);
      setDraft("");
    },
    [tags, onChange],
  );

  const removeTag = useCallback(
    (index: number) => {
      onChange(tags.filter((_, i) => i !== index));
    },
    [tags, onChange],
  );

  return (
    <div className="shadow-card rounded-lg bg-paper-hi">
      <div className="hair-b border-b px-4 py-3">
        <h3 className="text-[13px] font-semibold">Tags</h3>
      </div>
      <div className="p-4">
        <div className="hair flex flex-wrap items-center gap-1.5 rounded-md border bg-paper px-2 py-1.5 focus-within:border-tide">
          {tags.map((tag, i) => (
            <span
              key={`${tag}-${i}`}
              className="inline-flex items-center gap-1 rounded bg-tide-ss px-1.5 py-0.5 text-[11.5px] text-tide"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(i)}
                aria-label={`Remove ${tag}`}
                className="text-tide/70 hover:text-tide"
              >
                <X size={11} strokeWidth={2.2} />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addTag(draft);
              } else if (
                e.key === "Backspace" &&
                draft.length === 0 &&
                tags.length > 0
              ) {
                removeTag(tags.length - 1);
              }
            }}
            onBlur={() => {
              if (draft.trim()) addTag(draft);
            }}
            placeholder={tags.length === 0 ? "Add tags…" : ""}
            className="min-w-[80px] flex-1 bg-transparent text-[12.5px] text-ink-2 placeholder:text-ink-5 focus:outline-none"
          />
        </div>
        <p className="mt-1.5 text-[11px] text-ink-5">
          Press Enter or comma to add a tag.
        </p>
      </div>
    </div>
  );
}
